import { faArrowLeft, faSearch } from '@fortawesome/pro-light-svg-icons';
import { Dispatch, SetStateAction } from 'react';
import { tv } from 'tailwind-variants';

import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { Input } from '@/components/ui/input/input.component';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { SheetHeader } from '@/components/ui/sheet';
import {
  ProgressiveSearchModalKeys,
  SelectedProcedures,
} from '@/app/(main)/_components/search-modal/search-modal.types';
import { SelectMoreResults } from '@/app/(main)/_components/search-modal/_components/select-more-results.component';
import { useSelectMoreExams } from '@/app/(main)/_components/search-modal/_hooks/use-select-more-exams.hook';

const selectedMoreExamsStyle = tv({
  slots: {
    seeMoreContainer: [
      'data-[show=false]:hidden data-[show=true]:opacity-1',
      'data-[show=false]:animate-fade-out-bottom-procedure data-[show=true]:animate-fade-in-bottom-procedure',
      'transition-all duration-300 rounded-b-md md:max-w-[800px] w-full bg-white',
      'flex justify-between items-center text-center mt-6',
      'self-end',
    ],
  },
  variants: {
    isOnMobileDevice: {
      true: {
        seeMoreContainer: 'pt-[-50px]',
      },
    },
  },
});

export interface SelectMoreExamsProps {
  selectedProcedures: SelectedProcedures[];
  firstProcedureSelected: SelectedProcedures;
  onClear: () => void;
  setSelectProcedures: Dispatch<SetStateAction<SelectedProcedures[]>>;
}

export function SelectMoreExams({
  selectedProcedures,
  firstProcedureSelected,
  onClear,
  setSelectProcedures,
}: SelectMoreExamsProps) {
  const { handleJump: jump } = useProgressiveModal<ProgressiveSearchModalKeys>();
  const { seeMoreContainer } = selectedMoreExamsStyle();

  const hasExtraSelectedProcedures
    = selectedProcedures.filter((item) => item.id !== firstProcedureSelected.id).length > 0;

  const handleJump = (key: ProgressiveSearchModalKeys, returning = false) => {
    if (!jump) {
      return;
    }

    if (hasExtraSelectedProcedures) {
      jump({ key: ProgressiveSearchModalKeys.ATTENTION_TO_LOSE_LABORATORY_PROCEDURE, returning: true });
      return;
    }

    jump({ key, returning });

    if (returning && onClear && key !== ProgressiveSearchModalKeys.ATTENTION_TO_LOSE_LABORATORY_PROCEDURE) {
      onClear();
    }
  };

  const {
    searchDisplayValue,
    handleSearchInput,
    inputRef,
    searchResults,
    emptySearchResults,
    isLoadingLaboratoryProcedures,
    isLoadingMoreLaboratoryProcedures,
    handleSelectProcedure,
    isChecked,
    handleLoadMore,
    hasSearchResults,
    isSearching,
    hasNoMoreResults,
    showSeeSchedules,
    searchInputPlaceholder,
    addMoreExamsMessage,
    selectionExamsMessage,
    showMoreExamsTitleButton,
    selectedExamsCount,
    searchType,
  } = useSelectMoreExams({
    selectedProcedures,
    setSelectProcedures,
  });

  return (
    <div className="flex flex-col justify-between h-full p-5">
      <div>
        <SheetHeader className="flex flex-row items-start gap-4 mb-4">
          <button
            data-testid="back-button--select-more-exams"
            className="pt-5"
            onClick={() => {
              if (selectedProcedures.length === 0) {
                handleJump(ProgressiveSearchModalKeys.SEARCH, true);
                return;
              }
              handleJump(ProgressiveSearchModalKeys.ATTENTION_TO_LOSE_LABORATORY_PROCEDURE, true);
            }}
          >
            <Icon icon={faArrowLeft} size="xl" className="text-gray-500 self-center cursor-pointer" />
          </button>
          <Input
            data-testid="search-input--select-more-exams"
            placeholder={searchInputPlaceholder}
            startIcon={faSearch}
            startIconStyle="text-gray-500 text-xl"
            value={searchDisplayValue}
            onChange={handleSearchInput}
            ref={inputRef}
            className="w-full"
          />
        </SheetHeader>

        {emptySearchResults && (
          <p className="text-base font-gray-800 mt-6 text-left" data-testid="search-empty-results--select-more-exams">
            Não encontramos resultados para
            {' '}
            <strong>{searchDisplayValue}</strong>
            . Tente outra palavra.
          </p>
        )}
      </div>

      {!emptySearchResults && (
        <>
          {!hasSearchResults && !isSearching && (
            <>
              {isChecked(firstProcedureSelected)
                ? (
                    <p className="text-gray-800" data-testid="select-procedure-paragraph--select-more-exams">
                      Você selecionou
                      {' '}
                      <strong>{firstProcedureSelected.displayName}</strong>
                      .
                      {' '}
                      {addMoreExamsMessage}
                    </p>
                  )
                : (
                    <p className="text-gray-800" data-testid="select-procedure-paragraph--select-more-exams">
                      {selectionExamsMessage}
                    </p>
                  )}
              <span className="mt-6">Sugestões:</span>
            </>
          )}

          <div className="flex flex-col md:basis-[40vh] -mx-5 overflow-y-scroll h-full">
            <SelectMoreResults
              isLoading={isLoadingLaboratoryProcedures}
              searchResults={searchResults}
              searchType={searchType}
              handleSelectProcedure={handleSelectProcedure}
              isChecked={isChecked}
            />

            {!hasNoMoreResults && hasSearchResults && !isLoadingLaboratoryProcedures && (
              <div className="px-5 mt-5">
                <Button
                  data-testid="load-more--select-more-exams"
                  variant="secondary"
                  isLoading={isLoadingMoreLaboratoryProcedures}
                  disabled={isLoadingMoreLaboratoryProcedures}
                  onClick={handleLoadMore}
                >
                  {showMoreExamsTitleButton}
                </Button>
              </div>
            )}
          </div>
        </>
      )}

      <div
        data-testid="see-more-container--select-more-exams"
        data-show={selectedProcedures.length > 0 && showSeeSchedules}
        className={seeMoreContainer({
          isOnMobileDevice: showSeeSchedules && selectedProcedures.length > 0,
        })}
      >
        <span>{selectedExamsCount}</span>
        <Button
          data-testid="see-schedules-button--select-more-exams"
          className="w-fit"
          onClick={() => jump({ key: ProgressiveSearchModalKeys.SELECTED_OVERVIEW_EXAMS })}
        >
          Continuar
        </Button>
      </div>
    </div>
  );
}
